import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  fetchUserPreferences, 
  saveUserPreferences 
} from '../../../state/mealPlannerSlice';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaCheck, 
  FaSave, 
  FaUtensils, 
  FaPlus, 
  FaTimes, 
  FaAppleAlt, 
  FaHeart, 
  FaGlobeAmericas, 
  FaAllergies,
  FaGraduationCap,
  FaClock,
  FaWeight,
  FaSun,
  FaMoon,
  FaCoffee,
  FaHamburger,
  FaExclamationTriangle,
  FaEdit
} from 'react-icons/fa';

const dietaryRestrictionOptions = [
  { id: 'vegan', label: 'Vegan' },
  { id: 'vegetarian', label: 'Vegetarian' },
  { id: 'glutenFree', label: 'Gluten-Free' },
  { id: 'dairyFree', label: 'Dairy-Free' },
  { id: 'keto', label: 'Keto' },
  { id: 'paleo', label: 'Paleo' },
  { id: 'lowCarb', label: 'Low Carb' },
  { id: 'lowFat', label: 'Low Fat' },
];

const cuisineOptions = [
  { id: 'italian', label: 'Italian' },
  { id: 'mexican', label: 'Mexican' },
  { id: 'indian', label: 'Indian' },
  { id: 'chinese', label: 'Chinese' },
  { id: 'japanese', label: 'Japanese' },
  { id: 'thai', label: 'Thai' },
  { id: 'mediterranean', label: 'Mediterranean' },
  { id: 'american', label: 'American' },
  { id: 'french', label: 'French' },
];

const healthGoalOptions = [
  { id: 'weightLoss', label: 'Weight Loss' },
  { id: 'muscleGain', label: 'Muscle Gain' },
  { id: 'maintenance', label: 'Maintenance' },
  { id: 'heartHealth', label: 'Heart Health' },
  { id: 'energyBoost', label: 'Energy Boost' },
];

const skillLevelOptions = [
  { id: 'beginner', label: 'Beginner' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'advanced', label: 'Advanced' },
];

export default function PreferenceForm({ onComplete }) {
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const { userPreferences, status } = useSelector(state => state.mealPlanner);
  
  // Form state
  const [formData, setFormData] = useState({
    dietaryRestrictions: [],
    customDietaryRestrictions: [],
    healthGoals: '',
    customHealthGoals: [],
    cuisinePreferences: [],
    customCuisinePreferences: [],
    allergies: [],
    cookingSkillLevel: 'intermediate',
    cookingTimePreference: 30,
    mealsPerDay: 3,
    portionSize: '2',
    isCustomPortionSize: false,
    customPortionSize: '',
    maxCaloriesPerMeal: 600,
    preferredMealTypes: ['breakfast', 'lunch', 'dinner'] // Default to all meals
  });
  
  // Error message state
  const [errorMessage, setErrorMessage] = useState('');
  
  // Clear error message after 3 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);
  
  // Initialize form data from user preferences
  useEffect(() => {
    if (user?.id && !userPreferences) {
      console.log("Fetching user preferences...");
      dispatch(fetchUserPreferences(user.id));
    }
  }, [user, userPreferences, dispatch]);

  // Process user preferences when they are loaded
  useEffect(() => {
    if (userPreferences) {
      console.log("Loading user preferences:", userPreferences);
      
      // Helper function to determine if a value is a standard option
      const isStandardOption = (value, options) => {
        return options.some(option => option.id === value);
      };
      
      // Process dietary restrictions
      const standardDiets = userPreferences.dietaryRestrictions?.filter(item => 
        isStandardOption(item, dietaryRestrictionOptions)) || [];
      
      const customDiets = userPreferences.dietaryRestrictions?.filter(item => 
        !isStandardOption(item, dietaryRestrictionOptions)) || [];
        
      // Process health goals
      const standardHealthGoal = isStandardOption(userPreferences.healthGoals, healthGoalOptions) 
        ? userPreferences.healthGoals 
        : '';
        
      const customHealthGoals = !isStandardOption(userPreferences.healthGoals, healthGoalOptions) && 
        userPreferences.healthGoals 
        ? [userPreferences.healthGoals] 
        : [];
      
      // Process cuisine preferences
      const standardCuisines = userPreferences.cuisinePreferences?.filter(item => 
        isStandardOption(item, cuisineOptions)) || [];
        
      const customCuisines = userPreferences.cuisinePreferences?.filter(item => 
        !isStandardOption(item, cuisineOptions)) || [];
      
      // Process preferred meal types
      const preferredMealTypes = userPreferences.preferredMealTypes || ['breakfast', 'lunch', 'dinner'];
      
      // Ensure preferredMealTypes doesn't exceed mealsPerDay
      const mealsPerDay = userPreferences.mealsPerDay || 3;
      const validatedMealTypes = preferredMealTypes.length > mealsPerDay 
        ? preferredMealTypes.slice(0, mealsPerDay) 
        : preferredMealTypes;

      // If no meal types are selected but meals per day is set, default to first available options
      const finalMealTypes = validatedMealTypes.length === 0 && mealsPerDay > 0
        ? ['breakfast', 'lunch', 'dinner'].slice(0, mealsPerDay)
        : validatedMealTypes;
      
      // Process portion size
      let isCustomPortionSize = false;
      let portionSize = '2'; // Default to 2 servings
      let customPortionSize = '';

      if (userPreferences.portionSize) {
        // Check if it's one of our standard sizes (1, 2, 3, 4)
        if (['1', '2', '3', '4'].includes(userPreferences.portionSize)) {
          portionSize = userPreferences.portionSize;
        } else {
          // It's a custom value
          isCustomPortionSize = true;
          portionSize = 'custom';
          customPortionSize = userPreferences.portionSize;
        }
      }
      
      // Update form data
      setFormData({
        dietaryRestrictions: standardDiets,
        customDietaryRestrictions: customDiets,
        healthGoals: standardHealthGoal,
        customHealthGoals: customHealthGoals,
        cuisinePreferences: standardCuisines,
        customCuisinePreferences: customCuisines,
        allergies: userPreferences.allergies || [],
        cookingSkillLevel: userPreferences.cookingSkillLevel || 'intermediate',
        cookingTimePreference: userPreferences.cookingTimePreference || 30,
        mealsPerDay: mealsPerDay,
        portionSize: portionSize,
        isCustomPortionSize: isCustomPortionSize,
        customPortionSize: customPortionSize,
        maxCaloriesPerMeal: userPreferences.maxCaloriesPerMeal || 600,
        preferredMealTypes: finalMealTypes
      });
      
      console.log("Form data initialized with standard and custom values");
    }
  }, [userPreferences]);
  
  // Handle checkbox changes
  const handleCheckboxChange = (field, value) => {
    setFormData(prev => {
      // If the value exists in the array, remove it; otherwise, add it
      const newValues = prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value];
      
      return { ...prev, [field]: newValues };
    });
  };
  
  // Handle radio button changes
  const handleRadioChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Handle range input changes
  const handleRangeChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: parseInt(value) }));
  };
  
  // Handle text input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Handle adding a new allergy
  const handleAddAllergy = (e) => {
    e.preventDefault();
    const allergyInput = document.getElementById('allergy-input');
    const allergy = allergyInput.value.trim();
    
    if (allergy && !formData.allergies.includes(allergy)) {
      setFormData(prev => ({
        ...prev,
        allergies: [...prev.allergies, allergy]
      }));
      allergyInput.value = '';
    }
  };
  
  // Handle removing an allergy
  const handleRemoveAllergy = (allergy) => {
    setFormData(prev => ({
      ...prev,
      allergies: prev.allergies.filter(item => item !== allergy)
    }));
  };
  
  // Add custom dietary restriction
  const handleAddCustomDietaryRestriction = (e) => {
    e.preventDefault();
    const inputElement = document.getElementById('custom-diet-input');
    const customDiet = inputElement.value.trim();
    
    if (!customDiet) return;
    
    // Check if this matches any predefined option by label
    const matchedOption = dietaryRestrictionOptions.find(
      option => option.label.toLowerCase() === customDiet.toLowerCase()
    );
    
    if (matchedOption) {
      // If it matches a predefined option, select that option instead
      if (!formData.dietaryRestrictions.includes(matchedOption.id)) {
        setFormData(prev => ({
          ...prev,
          dietaryRestrictions: [...prev.dietaryRestrictions, matchedOption.id]
        }));
      }
    } else if (!formData.customDietaryRestrictions.includes(customDiet)) {
      // It's a truly custom value, add it to custom list
      const formattedValue = customDiet.charAt(0).toUpperCase() + customDiet.slice(1).toLowerCase();
      
      setFormData(prev => ({
        ...prev,
        customDietaryRestrictions: [...prev.customDietaryRestrictions, formattedValue]
      }));
    }
    
    // Clear the input
    inputElement.value = '';
  };
  
  // Remove custom dietary restriction
  const handleRemoveCustomDietaryRestriction = (item) => {
    setFormData(prev => ({
      ...prev,
      customDietaryRestrictions: prev.customDietaryRestrictions.filter(i => i !== item)
    }));
  };
  
  // Add custom health goal
  const handleAddCustomHealthGoal = (e) => {
    e.preventDefault();
    const inputElement = document.getElementById('custom-goal-input');
    const customGoal = inputElement.value.trim();
    
    if (!customGoal) return;
    
    // Check if this matches any predefined option by label
    const matchedOption = healthGoalOptions.find(
      option => option.label.toLowerCase() === customGoal.toLowerCase()
    );
    
    if (matchedOption) {
      // If it matches a predefined option, select that option instead
      setFormData(prev => ({
        ...prev,
        healthGoals: matchedOption.id,
        customHealthGoals: [] // Clear any custom goals
      }));
    } else if (!formData.customHealthGoals.includes(customGoal)) {
      // It's a truly custom value, add it to custom list
      const formattedValue = customGoal.charAt(0).toUpperCase() + customGoal.slice(1).toLowerCase();
      
      // For health goals, unselect any predefined option
      setFormData(prev => ({
        ...prev,
        healthGoals: '',
        customHealthGoals: [...prev.customHealthGoals, formattedValue]
      }));
    }
    
    // Clear the input
    inputElement.value = '';
  };
  
  // Remove custom health goal
  const handleRemoveCustomHealthGoal = (goal) => {
    setFormData(prev => ({
      ...prev,
      customHealthGoals: prev.customHealthGoals.filter(g => g !== goal)
    }));
  };
  
  // Add custom cuisine preference
  const handleAddCustomCuisine = (e) => {
    e.preventDefault();
    const inputElement = document.getElementById('custom-cuisine-input');
    const customCuisine = inputElement.value.trim();
    
    if (!customCuisine) return;
    
    // Check if this matches any predefined option by label
    const matchedOption = cuisineOptions.find(
      option => option.label.toLowerCase() === customCuisine.toLowerCase()
    );
    
    if (matchedOption) {
      // If it matches a predefined option, select that option instead
      if (!formData.cuisinePreferences.includes(matchedOption.id)) {
        setFormData(prev => ({
          ...prev,
          cuisinePreferences: [...prev.cuisinePreferences, matchedOption.id]
        }));
      }
    } else if (!formData.customCuisinePreferences.includes(customCuisine)) {
      // It's a truly custom value, add it to custom list
      const formattedValue = customCuisine.charAt(0).toUpperCase() + customCuisine.slice(1).toLowerCase();
      
      setFormData(prev => ({
        ...prev,
        customCuisinePreferences: [...prev.customCuisinePreferences, formattedValue]
      }));
    }
    
    // Clear the input
    inputElement.value = '';
  };
  
  // Remove custom cuisine preference
  const handleRemoveCustomCuisine = (cuisine) => {
    setFormData(prev => ({
      ...prev,
      customCuisinePreferences: prev.customCuisinePreferences.filter(c => c !== cuisine)
    }));
  };
  
  // Handle meal type selection
  const handleMealTypeToggle = (mealType) => {
    setFormData(prevData => {
      // Make a copy of current meal types
      const currentTypes = [...prevData.preferredMealTypes];
      
      if (currentTypes.includes(mealType)) {
        // Don't allow deselecting if this would leave fewer meal types than required
        if (currentTypes.length <= prevData.mealsPerDay) {
          // Only allow deselection if there will still be at least one meal type left
          if (currentTypes.length > 1) {
            return { 
              ...prevData, 
              preferredMealTypes: currentTypes.filter(type => type !== mealType) 
            };
          } else {
            setErrorMessage("You must select at least one meal type");
            return prevData;
          }
        } else {
          // More selected than needed, so removing is fine
          return { 
            ...prevData, 
            preferredMealTypes: currentTypes.filter(type => type !== mealType) 
          };
        }
      } else {
        // Adding a new meal type
        if (currentTypes.length < prevData.mealsPerDay) {
          // We have room to add another meal type
          return { 
            ...prevData, 
            preferredMealTypes: [...currentTypes, mealType] 
          };
        } else {
          // Already have enough meal types selected
          setErrorMessage(`You can select exactly ${prevData.mealsPerDay} meal type(s)`);
          return prevData;
        }
      }
    });
  };
  
  // Update preferred meal types when mealsPerDay changes
  useEffect(() => {
    setFormData(prevData => {
      let updatedTypes = [...prevData.preferredMealTypes];
      
      // If more meal types selected than meals per day, trim the list
      if (updatedTypes.length > prevData.mealsPerDay) {
        updatedTypes = updatedTypes.slice(0, prevData.mealsPerDay);
      }
      
      // If no meal types selected, set defaults based on mealsPerDay
      if (updatedTypes.length === 0) {
        if (prevData.mealsPerDay === 1) {
          updatedTypes = ['lunch'];
        } else if (prevData.mealsPerDay === 2) {
          updatedTypes = ['lunch', 'dinner'];
        } else {
          updatedTypes = ['breakfast', 'lunch', 'dinner'];
        }
      }
      
      return { ...prevData, preferredMealTypes: updatedTypes };
    });
  }, [formData.mealsPerDay]);
  
  // Handle custom portion size input change
  const handleCustomPortionSizeChange = (e) => {
    const value = e.target.value.trim();
    setFormData(prev => ({
      ...prev,
      customPortionSize: value
    }));
  };

  // Toggle custom portion size mode
  const toggleCustomPortionSize = () => {
    setFormData(prev => {
      if (prev.isCustomPortionSize) {
        // Switching from custom to standard
        return {
          ...prev,
          isCustomPortionSize: false,
          portionSize: '2' // Default to 2 servings
        };
      } else {
        // Switching to custom
        return {
          ...prev,
          isCustomPortionSize: true,
          portionSize: 'custom'
        };
      }
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate if user has selected the correct number of meal types
    if (formData.preferredMealTypes.length !== formData.mealsPerDay) {
      setErrorMessage(`Please select exactly ${formData.mealsPerDay} meal type(s)`);
      return;
    }
    
    // Validate custom portion size if it's enabled
    if (formData.isCustomPortionSize && (!formData.customPortionSize || isNaN(formData.customPortionSize))) {
      setErrorMessage("Please enter a valid number for custom portion size");
      return;
    }
    
    // Combine standard and custom values
    const allDietaryRestrictions = [
      ...formData.dietaryRestrictions,
      ...formData.customDietaryRestrictions
    ].filter(Boolean); // Remove any empty values
    
    // For health goals, use either the standard selection or the first custom goal
    const selectedHealthGoal = formData.healthGoals || 
      (formData.customHealthGoals.length > 0 ? formData.customHealthGoals[0] : '');
    
    const allCuisinePreferences = [
      ...formData.cuisinePreferences,
      ...formData.customCuisinePreferences
    ].filter(Boolean); // Remove any empty values
    
    // Filter allergies to remove any empty strings
    const cleanedAllergies = formData.allergies.filter(Boolean);
    
    // Determine final portion size value
    const finalPortionSize = formData.isCustomPortionSize 
      ? formData.customPortionSize
      : formData.portionSize;
    
    // Create a clean submission data object with only the fields the backend expects
    const submissionData = {
      dietaryRestrictions: allDietaryRestrictions,
      healthGoals: selectedHealthGoal,
      cuisinePreferences: allCuisinePreferences,
      allergies: cleanedAllergies,
      cookingSkillLevel: formData.cookingSkillLevel,
      cookingTimePreference: formData.cookingTimePreference,
      mealsPerDay: formData.mealsPerDay,
      portionSize: finalPortionSize,
      maxCaloriesPerMeal: formData.maxCaloriesPerMeal,
      preferredMealTypes: formData.preferredMealTypes
    };
    
    console.log("Submitting preferences:", submissionData);
    
    if (user?.id) {
      try {
        const result = await dispatch(saveUserPreferences({
          userId: user.id,
          preferences: submissionData
        })).unwrap();
        
        console.log("Preferences saved successfully:", result);
        
        if (onComplete) {
          onComplete();
        }
      } catch (error) {
        console.error("Error saving preferences:", error);
      }
    }
  };
  
  // Section animations
  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-5xl mx-auto text-pri-white"
    >
      <h2 className="text-2xl font-bold mb-6 text-center bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text">
        Your Meal Preferences
      </h2>
      <p className="text-gray-300 text-center mb-8 max-w-3xl mx-auto">
        Customize your meal preferences to help us generate meal plans that perfectly match your lifestyle and tastes.
      </p>
      
      {/* Error Message */}
      <AnimatePresence>
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-gradient-to-r from-red-500/20 to-red-600/20 border border-red-500/30 rounded-lg p-3 mb-4 text-white flex items-center"
          >
            <FaExclamationTriangle className="text-red-400 mr-2" />
            <span>{errorMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>
      
      <form onSubmit={handleSubmit} className="space-y-10">
        {/* Dietary Restrictions Section */}
        <motion.div 
          className="bg-gradient-to-br from-sec-dark to-pri-dark rounded-xl overflow-hidden shadow-lg border border-gray-700"
          initial="hidden"
          animate="visible"
          variants={sectionVariants}
        >
          <div className="bg-gradient-to-r from-soft-pink/10 to-mint-green/10 p-4 border-b border-gray-700">
            <div className="flex items-center">
              <FaAppleAlt className="text-soft-pink mr-3 text-xl" />
              <h3 className="text-xl font-semibold bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text">
                Dietary Restrictions
              </h3>
            </div>
            <p className="text-gray-400 text-sm mt-1">Select any dietary restrictions you follow</p>
          </div>
          
          <div className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {dietaryRestrictionOptions.map(option => (
                <div key={option.id} className="relative">
                  <input
                    id={`diet-${option.id}`}
                    type="checkbox"
                    checked={formData.dietaryRestrictions.includes(option.id)}
                    onChange={() => handleCheckboxChange('dietaryRestrictions', option.id)}
                    className="peer sr-only"
                  />
                  <label
                    htmlFor={`diet-${option.id}`}
                    className="flex items-center justify-between p-3 text-pri-white bg-pri-dark/60 border border-white/10 rounded-lg cursor-pointer peer-checked:border-soft-pink peer-checked:bg-gradient-to-br from-soft-pink/20 to-mint-green/20 hover:bg-pri-dark/40 transition-all duration-300"
                  >
                    <div className="w-full text-sm font-medium">{option.label}</div>
                    {formData.dietaryRestrictions.includes(option.id) && (
                      <FaCheck className="text-mint-green" />
                    )}
                  </label>
                </div>
              ))}
            </div>
            
            {/* Custom Dietary Restrictions */}
            <div className="mt-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {formData.customDietaryRestrictions.map(diet => (
                  <div key={diet} className="relative">
                    <input
                      id={`diet-custom-${diet}`}
                      type="checkbox"
                      checked={true}
                      className="peer sr-only"
                      readOnly
                    />
                    <label
                      htmlFor={`diet-custom-${diet}`}
                      className="flex items-center justify-between p-3 text-pri-white bg-pri-dark/60 border border-white/10 rounded-lg cursor-pointer peer-checked:border-soft-pink peer-checked:bg-gradient-to-br from-soft-pink/20 to-mint-green/20 hover:bg-pri-dark/40 transition-all duration-300"
                      onClick={() => handleRemoveCustomDietaryRestriction(diet)}
                    >
                      <div className="w-full text-sm font-medium">{diet}</div>
                      <div className="flex items-center">
                        <FaCheck className="text-mint-green mr-2" />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveCustomDietaryRestriction(diet);
                          }}
                          className="text-gray-400 hover:text-soft-pink"
                          title="Remove"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
              <div className="flex mt-4">
                <div className="w-full">
                  <p className="text-xs text-gray-400 mb-2">Add your own dietary restriction:</p>
                  <div className="flex">
                    <input
                      id="custom-diet-input"
                      type="text"
                      placeholder="Add a custom dietary restriction"
                      className="flex-grow p-2 border border-white/10 rounded-l-lg focus:outline-none focus:border-soft-pink bg-pri-dark/40 text-pri-white"
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomDietaryRestriction}
                      className="px-4 py-2 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark rounded-r-lg hover:opacity-90 transition-opacity duration-300"
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Health Goals Section */}
        <motion.div 
          className="bg-gradient-to-br from-sec-dark to-pri-dark rounded-xl overflow-hidden shadow-lg border border-gray-700"
          initial="hidden"
          animate="visible"
          variants={sectionVariants}
        >
          <div className="bg-gradient-to-r from-soft-pink/10 to-mint-green/10 p-4 border-b border-gray-700">
            <div className="flex items-center">
              <FaHeart className="text-soft-pink mr-3 text-xl" />
              <h3 className="text-xl font-semibold bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text">
                Health Goals
              </h3>
            </div>
            <p className="text-gray-400 text-sm mt-1">Select your primary health and fitness goal</p>
          </div>
          
          <div className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {healthGoalOptions.map(option => (
                <div key={option.id} className="relative">
                  <input
                    id={`goal-${option.id}`}
                    type="radio"
                    name="healthGoals"
                    value={option.id}
                    checked={formData.healthGoals === option.id}
                    onChange={() => {
                      // Clear any custom health goals when selecting a predefined one
                      setFormData(prev => ({
                        ...prev,
                        healthGoals: option.id,
                        customHealthGoals: []
                      }));
                    }}
                    className="peer sr-only"
                  />
                  <label
                    htmlFor={`goal-${option.id}`}
                    className="flex items-center justify-between p-3 text-pri-white bg-pri-dark/60 border border-white/10 rounded-lg cursor-pointer peer-checked:border-soft-pink peer-checked:bg-gradient-to-br from-soft-pink/20 to-mint-green/20 hover:bg-pri-dark/40 transition-all duration-300"
                  >
                    <div className="w-full text-sm font-medium">{option.label}</div>
                    {formData.healthGoals === option.id && (
                      <FaCheck className="text-mint-green" />
                    )}
                  </label>
                </div>
              ))}
              
              {/* Custom Health Goals */}
              {formData.customHealthGoals.map(goal => (
                <div key={goal} className="relative">
                  <input
                    id={`goal-custom-${goal}`}
                    type="radio"
                    name="healthGoals"
                    checked={formData.healthGoals === '' && formData.customHealthGoals.includes(goal)}
                    className="peer sr-only"
                    readOnly
                  />
                  <label
                    htmlFor={`goal-custom-${goal}`}
                    className="flex items-center justify-between p-3 text-pri-white bg-pri-dark/60 border border-white/10 rounded-lg cursor-pointer peer-checked:border-soft-pink peer-checked:bg-gradient-to-br from-soft-pink/20 to-mint-green/20 hover:bg-pri-dark/40 transition-all duration-300"
                  >
                    <div className="w-full text-sm font-medium">{goal}</div>
                    <div className="flex items-center">
                      <FaCheck className="text-mint-green mr-2" />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveCustomHealthGoal(goal);
                        }}
                        className="text-gray-400 hover:text-soft-pink"
                        title="Remove"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </label>
                </div>
              ))}
            </div>
            
            <div className="flex mt-4">
              <div className="w-full">
                <p className="text-xs text-gray-400 mb-2">Add your own health goal:</p>
                <div className="flex">
                  <input
                    id="custom-goal-input"
                    type="text"
                    placeholder="Add a custom health goal"
                    className="flex-grow p-2 border border-white/10 rounded-l-lg focus:outline-none focus:border-soft-pink bg-pri-dark/40 text-pri-white"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomHealthGoal}
                    className="px-4 py-2 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark rounded-r-lg hover:opacity-90 transition-opacity duration-300"
                  >
                    <FaPlus />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Cuisine Preferences Section */}
        <motion.div 
          className="bg-gradient-to-br from-sec-dark to-pri-dark rounded-xl overflow-hidden shadow-lg border border-gray-700"
          initial="hidden"
          animate="visible"
          variants={sectionVariants}
        >
          <div className="bg-gradient-to-r from-soft-pink/10 to-mint-green/10 p-4 border-b border-gray-700">
            <div className="flex items-center">
              <FaGlobeAmericas className="text-soft-pink mr-3 text-xl" />
              <h3 className="text-xl font-semibold bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text">
                Favorite Cuisines
              </h3>
            </div>
            <p className="text-gray-400 text-sm mt-1">Select cuisines you enjoy eating</p>
          </div>
          
          <div className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {cuisineOptions.map(option => (
                <div key={option.id} className="relative">
                  <input
                    id={`cuisine-${option.id}`}
                    type="checkbox"
                    checked={formData.cuisinePreferences.includes(option.id)}
                    onChange={() => handleCheckboxChange('cuisinePreferences', option.id)}
                    className="peer sr-only"
                  />
                  <label
                    htmlFor={`cuisine-${option.id}`}
                    className="flex items-center justify-between p-3 text-pri-white bg-pri-dark/60 border border-white/10 rounded-lg cursor-pointer peer-checked:border-soft-pink peer-checked:bg-gradient-to-br from-soft-pink/20 to-mint-green/20 hover:bg-pri-dark/40 transition-all duration-300"
                  >
                    <div className="w-full text-sm font-medium">{option.label}</div>
                    {formData.cuisinePreferences.includes(option.id) && (
                      <FaCheck className="text-mint-green" />
                    )}
                  </label>
                </div>
              ))}
              
              {/* Custom Cuisine Preferences */}
              {formData.customCuisinePreferences.map(cuisine => (
                <div key={cuisine} className="relative">
                  <input
                    id={`cuisine-custom-${cuisine}`}
                    type="checkbox"
                    checked={true}
                    className="peer sr-only"
                    readOnly
                  />
                  <label
                    htmlFor={`cuisine-custom-${cuisine}`}
                    className="flex items-center justify-between p-3 text-pri-white bg-pri-dark/60 border border-white/10 rounded-lg cursor-pointer peer-checked:border-soft-pink peer-checked:bg-gradient-to-br from-soft-pink/20 to-mint-green/20 hover:bg-pri-dark/40 transition-all duration-300"
                    onClick={() => handleRemoveCustomCuisine(cuisine)}
                  >
                    <div className="w-full text-sm font-medium">{cuisine}</div>
                    <div className="flex items-center">
                      <FaCheck className="text-mint-green mr-2" />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveCustomCuisine(cuisine);
                        }}
                        className="text-gray-400 hover:text-soft-pink"
                        title="Remove"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </label>
                </div>
              ))}
            </div>
            
            <div className="flex mt-4">
              <div className="w-full">
                <p className="text-xs text-gray-400 mb-2">Add your own cuisine:</p>
                <div className="flex">
                  <input
                    id="custom-cuisine-input"
                    type="text"
                    placeholder="Add a custom cuisine"
                    className="flex-grow p-2 border border-white/10 rounded-l-lg focus:outline-none focus:border-soft-pink bg-pri-dark/40 text-pri-white"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomCuisine}
                    className="px-4 py-2 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark rounded-r-lg hover:opacity-90 transition-opacity duration-300"
                  >
                    <FaPlus />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Allergies Section */}
        <motion.div 
          className="bg-gradient-to-br from-sec-dark to-pri-dark rounded-xl overflow-hidden shadow-lg border border-gray-700"
          initial="hidden"
          animate="visible"
          variants={sectionVariants}
        >
          <div className="bg-gradient-to-r from-soft-pink/10 to-mint-green/10 p-4 border-b border-gray-700">
            <div className="flex items-center">
              <FaAllergies className="text-soft-pink mr-3 text-xl" />
              <h3 className="text-xl font-semibold bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text">
                Allergies and Intolerances
              </h3>
            </div>
            <p className="text-gray-400 text-sm mt-1">Add any allergies or food intolerances you have</p>
          </div>
          
          <div className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {formData.allergies.map(allergy => (
                <div key={allergy} className="relative">
                  <input
                    id={`allergy-${allergy}`}
                    type="checkbox"
                    checked={true}
                    className="peer sr-only"
                    readOnly
                  />
                  <label
                    htmlFor={`allergy-${allergy}`}
                    className="flex items-center justify-between p-3 text-pri-white bg-pri-dark/60 border border-white/10 rounded-lg cursor-pointer peer-checked:border-soft-pink peer-checked:bg-gradient-to-br from-soft-pink/20 to-mint-green/20 hover:bg-pri-dark/40 transition-all duration-300"
                    onClick={() => handleRemoveAllergy(allergy)}
                  >
                    <div className="w-full text-sm font-medium">{allergy}</div>
                    <div className="flex items-center">
                      <FaCheck className="text-mint-green mr-2" />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveAllergy(allergy);
                        }}
                        className="text-gray-400 hover:text-soft-pink"
                        title="Remove"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </label>
                </div>
              ))}
            </div>
            
            <div className="flex mt-4">
              <div className="w-full">
                <p className="text-xs text-gray-400 mb-2">Add your own allergy or intolerance:</p>
                <div className="flex">
                  <input
                    id="allergy-input"
                    type="text"
                    placeholder="Add an allergy or intolerance"
                    className="flex-grow p-2 border border-white/10 rounded-l-lg focus:outline-none focus:border-soft-pink bg-pri-dark/40 text-pri-white"
                  />
                  <button
                    type="button"
                    onClick={handleAddAllergy}
                    className="px-4 py-2 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark rounded-r-lg hover:opacity-90 transition-opacity duration-300"
                  >
                    <FaPlus />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Cooking Preferences Section */}
        <motion.div 
          className="bg-gradient-to-br from-sec-dark to-pri-dark rounded-xl overflow-hidden shadow-lg border border-gray-700"
          initial="hidden"
          animate="visible"
          variants={sectionVariants}
        >
          <div className="bg-gradient-to-r from-soft-pink/10 to-mint-green/10 p-4 border-b border-gray-700">
            <div className="flex items-center">
              <FaUtensils className="text-soft-pink mr-3 text-xl" />
              <h3 className="text-xl font-semibold bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text">
                Cooking Preferences
              </h3>
            </div>
            <p className="text-gray-400 text-sm mt-1">Tell us about your cooking style and preferences</p>
          </div>
          
          <div className="p-5 space-y-8">
            {/* Cooking Skill Level */}
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center">
                <FaGraduationCap className="text-soft-pink mr-2 text-base" />
                Cooking Skill Level
              </h4>
              <div className="grid grid-cols-3 gap-3">
                {skillLevelOptions.map(option => (
                  <div key={option.id} className="relative">
                    <input
                      id={`skill-${option.id}`}
                      type="radio"
                      name="cookingSkillLevel"
                      value={option.id}
                      checked={formData.cookingSkillLevel === option.id}
                      onChange={() => handleRadioChange('cookingSkillLevel', option.id)}
                      className="peer sr-only"
                    />
                    <label
                      htmlFor={`skill-${option.id}`}
                      className="flex items-center justify-between p-3 text-pri-white bg-pri-dark/60 border border-white/10 rounded-lg cursor-pointer peer-checked:border-soft-pink peer-checked:bg-gradient-to-br from-soft-pink/20 to-mint-green/20 hover:bg-pri-dark/40 transition-all duration-300"
                    >
                      <div className="w-full text-sm font-medium">{option.label}</div>
                      {formData.cookingSkillLevel === option.id && (
                        <FaCheck className="text-mint-green" />
                      )}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Cooking Time */}
            <div>
              <h4 className="text-white font-medium mb-2 flex items-center">
                <FaClock className="text-soft-pink mr-2 text-base" />
                Maximum Cooking Time
              </h4>
              <p className="text-sm text-pri-white/60 mb-4">Maximum time you want to spend cooking (per meal)</p>
              <div className="flex flex-col space-y-2 px-2">
                <input
                  type="range"
                  min="15"
                  max="120"
                  step="5"
                  value={formData.cookingTimePreference}
                  onChange={(e) => handleRangeChange('cookingTimePreference', e.target.value)}
                  className="w-full h-2 bg-pri-dark/60 rounded-lg appearance-none cursor-pointer accent-soft-pink border border-white/10"
                />
                <div className="flex justify-between text-xs text-pri-white/60">
                  <span>15 min</span>
                  <span>120 min</span>
                </div>
                <p className="text-center bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text font-medium">
                  {formData.cookingTimePreference} minutes
                </p>
              </div>
            </div>
            
            {/* Meals Per Day */}
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center">
                <FaUtensils className="text-soft-pink mr-2 text-base" />
                Meals Per Day
              </h4>
              <div className="flex justify-center space-x-4">
                {[1, 2, 3].map(num => (
                  <div key={num} className="relative">
                    <input
                      id={`meals-${num}`}
                      type="radio"
                      name="mealsPerDay"
                      value={num}
                      checked={formData.mealsPerDay === num}
                      onChange={() => handleRadioChange('mealsPerDay', num)}
                      className="peer sr-only"
                    />
                    <label
                      htmlFor={`meals-${num}`}
                      className="flex items-center justify-center w-12 h-12 text-pri-white bg-pri-dark/60 border border-white/10 rounded-full cursor-pointer peer-checked:bg-gradient-to-r from-soft-pink to-mint-green peer-checked:text-pri-dark hover:bg-pri-dark/40 transition-all duration-300"
                    >
                      {num}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Preferred Meal Types - Show only when mealsPerDay is less than 3 */}
            <AnimatePresence>
              {formData.mealsPerDay < 3 && (
                <motion.div 
                  className="bg-gradient-to-br from-sec-dark to-pri-dark rounded-xl overflow-hidden shadow-lg border border-gray-700"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="bg-gradient-to-r from-soft-pink/10 to-mint-green/10 p-4 border-b border-gray-700">
                    <div className="flex items-center">
                      <FaUtensils className="text-soft-pink mr-3 text-xl" />
                      <h3 className="text-xl font-semibold bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text">
                        Preferred Meal Types
                      </h3>
                    </div>
                    <p className="text-gray-400 text-sm mt-1">
                      Select which {formData.mealsPerDay === 1 ? 'meal' : 'meals'} you'd like in your plan
                    </p>
                    {formData.preferredMealTypes.length !== formData.mealsPerDay && (
                      <p className="text-red-500 text-xs mt-1 font-medium flex items-center">
                        <FaExclamationTriangle className="mr-1 text-red-500" />
                        You must select exactly {formData.mealsPerDay} meal type(s)
                      </p>
                    )}
                  </div>
                  
                  <div className="p-5">
                    <div className="flex flex-wrap gap-4">
                      <button
                        type="button"
                        onClick={() => handleMealTypeToggle('breakfast')}
                        className={`flex items-center py-2 px-4 rounded transition-all duration-300 ${
                          formData.preferredMealTypes.includes('breakfast')
                            ? 'bg-gradient-to-r from-soft-pink/30 to-mint-green/30 text-white border border-soft-pink/30' 
                            : 'bg-pri-dark/60 text-pri-white border border-white/10 hover:bg-pri-dark/40'
                        }`}
                      >
                        <FaCoffee className={`mr-2 ${formData.preferredMealTypes.includes('breakfast') ? 'text-soft-pink' : 'text-gray-400'}`} /> 
                        Breakfast
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMealTypeToggle('lunch')}
                        className={`flex items-center py-2 px-4 rounded transition-all duration-300 ${
                          formData.preferredMealTypes.includes('lunch')
                            ? 'bg-gradient-to-r from-soft-pink/30 to-mint-green/30 text-white border border-soft-pink/30' 
                            : 'bg-pri-dark/60 text-pri-white border border-white/10 hover:bg-pri-dark/40'
                        }`}
                      >
                        <FaHamburger className={`mr-2 ${formData.preferredMealTypes.includes('lunch') ? 'text-soft-pink' : 'text-gray-400'}`} /> 
                        Lunch
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMealTypeToggle('dinner')}
                        className={`flex items-center py-2 px-4 rounded transition-all duration-300 ${
                          formData.preferredMealTypes.includes('dinner')
                            ? 'bg-gradient-to-r from-soft-pink/30 to-mint-green/30 text-white border border-soft-pink/30' 
                            : 'bg-pri-dark/60 text-pri-white border border-white/10 hover:bg-pri-dark/40'
                        }`}
                      >
                        <FaUtensils className={`mr-2 ${formData.preferredMealTypes.includes('dinner') ? 'text-soft-pink' : 'text-gray-400'}`} /> 
                        Dinner
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Portion Size */}
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center">
                <FaWeight className="text-soft-pink mr-2 text-base" />
                Number of Servings
              </h4>
              
              <div className="flex flex-col space-y-4">
                {/* Number buttons in a row */}
                <div className="flex justify-center space-x-4">
                  {['1', '2', '3', '4'].map(size => (
                    <div key={size} className="relative">
                      <input
                        id={`portion-${size}`}
                        type="radio"
                        name="portionSize"
                        value={size}
                        checked={!formData.isCustomPortionSize && formData.portionSize === size}
                        onChange={() => {
                          setFormData(prev => ({
                            ...prev,
                            portionSize: size,
                            isCustomPortionSize: false
                          }));
                        }}
                        className="peer sr-only"
                      />
                      <label
                        htmlFor={`portion-${size}`}
                        className="flex items-center justify-center w-12 h-12 text-pri-white bg-pri-dark/60 border border-white/10 rounded-full cursor-pointer peer-checked:bg-gradient-to-r from-soft-pink to-mint-green peer-checked:text-pri-dark hover:bg-pri-dark/40 transition-all duration-300"
                      >
                        {size}
                      </label>
                    </div>
                  ))}
                </div>
                
                {/* Custom input option */}
                <div className="flex justify-center">
                  <button
                    type="button"
                    className="flex items-center text-sm text-gray-400 hover:text-soft-pink"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      isCustomPortionSize: !prev.isCustomPortionSize
                    }))}
                  >
                    {formData.isCustomPortionSize ? (
                      <>
                        <FaTimes className="mr-1" /> Use standard options
                      </>
                    ) : (
                      <>
                        <FaEdit className="mr-1" /> Enter custom count
                      </>
                    )}
                  </button>
                </div>
                
                {/* Custom serving count input */}
                {formData.isCustomPortionSize && (
                  <div className="mt-2 flex justify-center">
                    <input
                      id="custom-portion-size"
                      type="number"
                      min="1"
                      placeholder="Enter number of servings"
                      value={formData.customPortionSize}
                      onChange={handleCustomPortionSizeChange}
                      className="p-2 border border-white/10 rounded-lg focus:outline-none focus:border-soft-pink bg-pri-dark/40 text-pri-white w-52 text-center"
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* Maximum Calories Per Meal */}
            <div>
              <h4 className="text-white font-medium mb-2 flex items-center">
                <FaWeight className="text-soft-pink mr-2 text-base" />
                Maximum Calories Per Meal
              </h4>
              <p className="text-sm text-pri-white/60 mb-4">Set a calorie limit for each meal</p>
              <div className="flex flex-col space-y-2 px-2">
                <input
                  type="range"
                  min="300"
                  max="1200"
                  step="50"
                  value={formData.maxCaloriesPerMeal}
                  onChange={(e) => handleRangeChange('maxCaloriesPerMeal', e.target.value)}
                  className="w-full h-2 bg-pri-dark/60 rounded-lg appearance-none cursor-pointer accent-soft-pink border border-white/10"
                />
                <div className="flex justify-between text-xs text-pri-white/60">
                  <span>300 cal</span>
                  <span>1200 cal</span>
                </div>
                <p className="text-center bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text font-medium">
                  {formData.maxCaloriesPerMeal} calories
                </p>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Submit Button */}
        <motion.div 
          className="pt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { delay: 0.5 } }}
        >
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full px-6 py-4 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark rounded-xl hover:opacity-90 flex items-center justify-center font-bold text-lg transition-opacity duration-300 shadow-lg"
          >
            {status === 'loading' ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-pri-dark" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving Preferences...
              </span>
            ) : (
              <>
                <FaSave className="mr-2 text-xl" />
                Save Preferences
              </>
            )}
          </button>
        </motion.div>
      </form>
    </motion.div>
  );
} 